import { callInternal } from './internal.js';
import { normalizeAssetUrl } from '../utils/asset-url.js';

export interface TimedSnapshotStatsRow {
	key: string;
	name: string;
	shouldScreenshot: number;
	hasScreenshot: number;
	handled: number;
	overdue: number;
}

export interface TimedSnapshotStats {
	shouldScreenshot: number;
	hasScreenshot: number;
	handled: number;
	overdue: number;
	rows: TimedSnapshotStatsRow[];
}

export interface TimedSnapshotStatsResult {
	stats: TimedSnapshotStats;
	queryLabel: string;
}

export interface TimedSnapshotStore {
	store_id: string | number;
	store?: string;
	store_name?: string;
	has_device?: string | number;
	[k: string]: unknown;
}

export interface TimedSnapshotDevice {
	device_auto_id: string | number;
	channel_id?: string | number;
	device_id?: string | number;
	alias?: string;
	store_id?: string | number;
	device_status?: string | number;
	channel_status?: string | number;
	has_yuzhiwei?: string | number;
	has_yuzhiweis?: string | number;
	yuzhiwei_name?: string;
	name?: string;
	[k: string]: unknown;
}

export interface TimedSnapshotUser {
	user_id: string | number;
	user_name?: string;
	real_name?: string;
	account?: string;
	[k: string]: unknown;
}

export interface TimedSnapshotTemplateItem {
	item_id: string | number;
	item_name: string;
	category_id: string | number;
	template_id: string | number;
	raw?: Record<string, unknown>;
}

export interface TimedSnapshotPlan {
	plan_id?: string | number;
	id?: string | number;
	title?: string;
	name?: string;
	close_status?: string | number;
	status?: string | number;
	store_count?: string | number;
	execute_count?: string | number;
	create_time?: string;
	last_update_time?: string;
	execute_date?: { start_time?: string; end_time?: string } | Record<string, unknown>;
	month?: Array<string | number>;
	week?: Array<string | number>;
	today_count?: Record<string, unknown>;
	total_count?: Record<string, unknown>;
	store_list?: Array<string | number>;
	[k: string]: unknown;
}

export interface TimedSnapshotPicture {
	id?: string | number;
	pic_id?: string | number;
	plan_id?: string | number;
	store_id?: string | number;
	store_name?: string;
	device_auto_id?: string | number;
	device_id?: string | number;
	device_sn?: string | number;
	channel_id?: string | number;
	alias?: string;
	title?: string;
	pic_url?: string;
	pic_info?: string;
	pic_path?: string;
	img_url?: string;
	screenshot_time?: string;
	create_time?: string;
	last_update_time?: string;
	status?: string | number;
	ok_status?: string | number;
	handle_status?: string | number;
	item_id?: string | number;
	item_name?: string;
	user_id?: string | number;
	user_name?: string;
	real_name?: string;
	remark?: string;
	score?: string | number;
	total_score?: string | number;
	items?: unknown[];
	[k: string]: unknown;
}

export interface TimedSnapshotStoreStatistic {
	totalViolations: number;
	mostViolationStore?: string;
	mostViolationStoreCount: number;
	leastViolationStore?: string;
	leastViolationStoreCount: number;
	mostViolationItem?: string;
	mostViolationItemCount: number;
	leastViolationItem?: string;
	leastViolationItemCount: number;
	dailyTrendByItem: Array<{ date: string; value: number }>;
	dailyTrendByStore: Array<{ date: string; value: number }>;
	raw?: unknown;
}

export interface TimedSnapshotOperatorStatistic {
	shouldScreenshot: number;
	hasScreenshot: number;
	handled: number;
	overdue: number;
	raw?: unknown;
}

export interface TimedSnapshotOperatorRankRow {
	key: string;
	account: string;
	fullName: string;
	shouldHandle: number;
	handled: number;
	rank: number;
	raw?: unknown;
}

export interface TimedSnapshotViolationRankRow {
	key: string;
	name: string;
	violationCount: number;
	percentage?: string;
	details?: unknown[];
	raw?: unknown;
}

export interface TimedSnapshotPageResult<T> {
	ok: boolean;
	items: T[];
	msg?: string;
	total?: number;
}

export interface TimedSnapshotActionResult {
	ok: boolean;
	msg?: string;
	planId?: string | number;
}

export interface FetchTimedSnapshotStatsOptions {
	startDate: string;
	endDate: string;
	storeIds?: Array<string | number>;
	planId?: string | number;
	userId?: string | number;
}

export interface FetchTimedSnapshotPlansOptions {
	page?: number;
	count?: number;
	status?: string;
	storeId?: string | number;
}

export interface FetchTimedSnapshotPicturesOptions {
	page?: number;
	count?: number;
	startDate: string;
	endDate: string;
	tab: 'overdue' | 'unprocessed' | 'processed';
	storeIds?: Array<string | number>;
	planId?: string | number;
	okStatus?: string | number;
	itemId?: string | number;
	userId?: string | number;
}

export interface CreateTimedSnapshotTaskRequest {
	title: string;
	startDate: string;
	endDate: string;
	executeWeek: Array<string | number>;
	executeMonth: Array<string | number>;
	items: TimedSnapshotTemplateItem[];
	stores: TimedSnapshotStore[];
	devices: TimedSnapshotDevice[];
	users: TimedSnapshotUser[];
	times: string[];
}

export interface HandleTimedSnapshotPictureRequest {
	id: string | number;
	handleStatus: 1 | 2;
	item?: TimedSnapshotTemplateItem;
	userIds?: Array<string | number>;
	description?: string;
	withoutEvent?: boolean;
	score?: string | number;
}

const EMPTY_STATS: TimedSnapshotStats = {
	shouldScreenshot: 0,
	hasScreenshot: 0,
	handled: 0,
	overdue: 0,
	rows: [],
};

function responseMsg(code: number | string, msg?: string): string {
	return `${msg || 'Request failed'} (code=${code})`;
}

function asRecord(value: unknown): Record<string, unknown> | null {
	return value && typeof value === 'object' && !Array.isArray(value)
		? value as Record<string, unknown>
		: null;
}

function normalizeArray<T>(data: unknown): T[] {
	if (Array.isArray(data)) return data as T[];
	const root = asRecord(data);
	if (!root) return [];
	for (const key of ['items', 'list', 'rows', 'records', 'data', 'result'] as const) {
		const value = root[key];
		if (Array.isArray(value)) return value as T[];
		const nested = normalizeArray<T>(value);
		if (nested.length > 0) return nested;
	}
	return [];
}

function pickNumber(record: Record<string, unknown> | null, keys: string[]): number {
	if (!record) return 0;
	for (const key of keys) {
		const raw = record[key];
		if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
		if (typeof raw === 'string') {
			const normalized = raw.trim().replace(/,/g, '');
			if (normalized !== '') {
				const parsed = Number(normalized);
				if (Number.isFinite(parsed)) return parsed;
			}
		}
	}
	return 0;
}

function pickString(record: Record<string, unknown> | null, keys: string[], fallback = ''): string {
	if (!record) return fallback;
	for (const key of keys) {
		const raw = record[key];
		if (typeof raw === 'string' && raw.trim()) return raw.trim();
		if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
	}
	return fallback;
}

function firstRecord(record: Record<string, unknown> | null, keys: string[]): Record<string, unknown> | null {
	if (!record) return null;
	for (const key of keys) {
		const value = asRecord(record[key]);
		if (value) return value;
	}
	return null;
}

function cleanQuery(query: Record<string, unknown>): Record<string, string | number> {
	const next: Record<string, string | number> = {};
	for (const [key, value] of Object.entries(query)) {
		if (value == null || value === '') continue;
		if (Array.isArray(value)) {
			if (value.length > 0) next[key] = value.map((item) => String(item)).join(',');
			continue;
		}
		if (typeof value === 'number' || typeof value === 'string') next[key] = value;
	}
	return next;
}

function cleanBody(body: Record<string, unknown>): Record<string, unknown> {
	const next: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(body)) {
		if (value == null || value === '') continue;
		next[key] = value;
	}
	return next;
}

function numericId(value: string | number): string | number {
	if (typeof value === 'number') return value;
	const trimmed = value.trim();
	if (/^\d+$/.test(trimmed)) return Number(trimmed);
	return value;
}

function normalizeTimes(times: string[]): string[] {
	const seen = new Set<string>();
	const invalidIntervals: string[] = [];
	for (const value of times) {
		const text = String(value ?? '').trim();
		const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(text);
		if (!match) continue;
		if (Number(match[2]) % 5 !== 0) {
			invalidIntervals.push(`${match[1].padStart(2, '0')}:${match[2]}`);
			continue;
		}
		seen.add(`${match[1].padStart(2, '0')}:${match[2]}`);
	}
	if (invalidIntervals.length > 0) throw new Error('Execution time must be a multiple of 5 minutes');
	return Array.from(seen).sort().slice(0, 10);
}

function normalizeStatsRow(row: Record<string, unknown>, index: number): TimedSnapshotStatsRow {
	return {
		key: pickString(row, ['user_id', 'operator_id', 'executor_id', 'store_id', 'id'], `row-${index}`),
		name: pickString(row, ['user_name', 'real_name', 'operator_name', 'executor_name', 'store_name', 'name'], `Executor ${index + 1}`),
		shouldScreenshot: pickNumber(row, ['should_screenshot', 'shouldScreenshot', 'should_count', 'need_screenshot', 'total']),
		hasScreenshot: pickNumber(row, ['has_screenshot', 'hasScreenshot', 'has_count', 'screenshot_count', 'done_screenshot']),
		handled: pickNumber(row, ['handled', 'handle', 'handled_count', 'handle_count', 'processed']),
		overdue: pickNumber(row, ['overdue', 'overdue_count', 'expired', 'expired_count', 'exceed']),
	};
}

function normalizeOperatorStats(data: unknown): TimedSnapshotStats {
	const root = asRecord(data);
	const summary = firstRecord(root, ['summary', 'statistic', 'total', 'totals']) ?? root;
	const rows = normalizeArray<Record<string, unknown>>(data).map(normalizeStatsRow);
	const directStats: TimedSnapshotStats = {
		shouldScreenshot: pickNumber(summary, ['should_screenshot', 'shouldScreenshot', 'should_count', 'need_screenshot', 'total']),
		hasScreenshot: pickNumber(summary, ['has_screenshot', 'hasScreenshot', 'has_count', 'screenshot_count', 'done_screenshot']),
		handled: pickNumber(summary, ['handled', 'handle', 'handled_count', 'handle_count', 'processed']),
		overdue: pickNumber(summary, ['overdue', 'overdue_count', 'expired', 'expired_count', 'exceed']),
		rows,
	};
	const directHasValues = directStats.shouldScreenshot > 0
		|| directStats.hasScreenshot > 0
		|| directStats.handled > 0
		|| directStats.overdue > 0;
	if (directHasValues || rows.length === 0) return directStats;
	return rows.reduce<TimedSnapshotStats>((acc, row) => ({
		shouldScreenshot: acc.shouldScreenshot + row.shouldScreenshot,
		hasScreenshot: acc.hasScreenshot + row.hasScreenshot,
		handled: acc.handled + row.handled,
		overdue: acc.overdue + row.overdue,
		rows: acc.rows,
	}), { ...EMPTY_STATS, rows });
}

function extractTotal(data: unknown): number | undefined {
	const root = asRecord(data);
	const total = pickNumber(root, ['total', 'count', 'current_count', 'total_count']);
	return total > 0 ? total : undefined;
}

export function getTimedSnapshotPlanId(plan: TimedSnapshotPlan): string | number | undefined {
	return plan.plan_id ?? plan.id;
}

export function getTimedSnapshotPlanTitle(plan: TimedSnapshotPlan): string {
	return plan.title || plan.name || String(getTimedSnapshotPlanId(plan) ?? '-');
}

export function isTimedSnapshotPlanEnabled(plan: TimedSnapshotPlan): boolean {
	const status = plan.close_status ?? plan.status ?? 0;
	return String(status) === '0';
}

export function getTimedSnapshotStoreName(store: TimedSnapshotStore): string {
	return store.store_name || store.store || String(store.store_id);
}

export function getTimedSnapshotUserName(user: TimedSnapshotUser): string {
	return user.real_name || user.user_name || user.account || String(user.user_id);
}

export function getTimedSnapshotDeviceName(device: TimedSnapshotDevice): string {
	return device.alias || device.name || String(device.device_id || device.device_auto_id);
}

export function getTimedSnapshotPictureUrl(picture: TimedSnapshotPicture | undefined): string {
	if (!picture) return '';
	return normalizeAssetUrl(picture.pic_url || picture.pic_info || picture.pic_path || picture.img_url);
}

export async function fetchTimedSnapshotStats(options: FetchTimedSnapshotStatsOptions): Promise<TimedSnapshotStatsResult> {
	const res = await callInternal<unknown>({
		short_name: 'standard',
		action: '/dianjian/statistic/operator',
		method: 'GET',
		query: cleanQuery({
			start_date: options.startDate,
			end_date: options.endDate,
			store_ids: options.storeIds,
			plan_id: options.planId,
			user_id: options.userId,
		}),
		av: '1',
	});
	if (Number(res.code) !== 0) throw new Error(responseMsg(res.code, res.msg));
	return { stats: normalizeOperatorStats(res.data), queryLabel: 'standard /dianjian/statistic/operator' };
}

export async function fetchTimedSnapshotStoreStatistic(options: FetchTimedSnapshotStatsOptions): Promise<TimedSnapshotStoreStatistic> {
	const res = await callInternal<unknown>({
		short_name: 'standard',
		action: '/dianjian/statistic/stores',
		method: 'GET',
		query: cleanQuery({
			start_date: options.startDate,
			end_date: options.endDate,
			store_ids: options.storeIds,
		}),
		av: '1',
	});
	if (Number(res.code) !== 0) throw new Error(responseMsg(res.code, res.msg));
	const root = asRecord(res.data);
	const mostStore = firstRecord(root, ['most_unqualified_store']);
	const leastStore = firstRecord(root, ['least_unqualified_store']);
	const mostItem = firstRecord(root, ['most_unqualified_item']);
	const leastItem = firstRecord(root, ['least_unqualified_item']);
	return {
		totalViolations: pickNumber(root, ['total_unqualified_pic', 'total_violations', 'unqualified_pic']),
		mostViolationStore: pickString(mostStore, ['store_name', 'name']),
		mostViolationStoreCount: pickNumber(mostStore, ['unqualified_pic', 'count', 'value']),
		leastViolationStore: pickString(leastStore, ['store_name', 'name']),
		leastViolationStoreCount: pickNumber(leastStore, ['unqualified_pic', 'count', 'value']),
		mostViolationItem: pickString(mostItem, ['item_name', 'name', 'AI']),
		mostViolationItemCount: pickNumber(mostItem, ['unqualified_pic', 'count', 'value']),
		leastViolationItem: pickString(leastItem, ['item_name', 'name', 'AI']),
		leastViolationItemCount: pickNumber(leastItem, ['unqualified_pic', 'count', 'value']),
		dailyTrendByItem: normalizeTrend(asRecord(firstRecord(root, ['daily_trend']))?.by_item),
		dailyTrendByStore: normalizeTrend(asRecord(firstRecord(root, ['daily_trend']))?.by_store),
		raw: res.data,
	};
}

export async function fetchTimedSnapshotOperatorStatistic(options: FetchTimedSnapshotStatsOptions): Promise<TimedSnapshotOperatorStatistic> {
	const result = await fetchTimedSnapshotStats(options);
	return { ...result.stats, raw: result };
}

export async function fetchTimedSnapshotOperatorRank(options: FetchTimedSnapshotStatsOptions & { page?: number; count?: number; sort?: string }): Promise<TimedSnapshotPageResult<TimedSnapshotOperatorRankRow>> {
	try {
		const res = await callInternal<unknown>({
			short_name: 'standard',
			action: '/dianjian/statistic/operator/rank/page',
			method: 'GET',
			query: cleanQuery({
				start_date: options.startDate,
				end_date: options.endDate,
				store_ids: options.storeIds,
				plan_id: options.planId,
				user_id: options.userId,
				sort: options.sort,
				page: options.page ?? 1,
				count: options.count ?? 20,
			}),
			av: '1',
		});
		if (Number(res.code) !== 0) return { ok: false, items: [], msg: responseMsg(res.code, res.msg) };
		const rows = normalizeArray<Record<string, unknown>>(res.data).map((row, index) => ({
			key: pickString(row, ['user_id', 'id', 'account'], `operator-${index}`),
			account: pickString(row, ['account', 'user_name', 'username'], '-'),
			fullName: pickString(row, ['username', 'real_name', 'full_name', 'role'], '-'),
			shouldHandle: pickNumber(row, ['should_handle', 'shouldHandle', 'to_be_processed']),
			handled: pickNumber(row, ['handled', 'processed', 'handle_count']),
			rank: pickNumber(row, ['rank']) || index + 1,
			raw: row,
		}));
		return { ok: true, items: rows, total: extractTotal(res.data) };
	} catch (e) {
		return { ok: false, items: [], msg: e instanceof Error ? e.message : String(e) };
	}
}

export async function fetchTimedSnapshotViolationRanking(
	kind: 'items' | 'stores',
	options: FetchTimedSnapshotStatsOptions & { page?: number; count?: number },
): Promise<TimedSnapshotPageResult<TimedSnapshotViolationRankRow>> {
	try {
		const res = await callInternal<unknown>({
			short_name: 'standard',
			action: kind === 'items' ? '/dianjian/statistic/unqualified/items/page' : '/dianjian/statistic/unqualified/stores/page',
			method: 'GET',
			query: cleanQuery({
				start_date: options.startDate,
				end_date: options.endDate,
				store_ids: options.storeIds,
				page: options.page ?? 1,
				count: options.count ?? 20,
			}),
			av: '1',
		});
		if (Number(res.code) !== 0) return { ok: false, items: [], msg: responseMsg(res.code, res.msg) };
		const rows = normalizeArray<Record<string, unknown>>(res.data).map((row, index) => ({
			key: pickString(row, ['item_id', 'store_id', 'id', 'item_name', 'store_name'], `${kind}-${index}`),
			name: pickString(row, ['item_name', 'store_name', 'AI', 'name'], '-'),
			violationCount: pickNumber(row, ['unqualified_pic', 'violation_numbers', 'count']),
			percentage: pickString(row, ['percentage', 'percent']),
			details: Array.isArray(row.unqualified_stores)
				? row.unqualified_stores
				: Array.isArray(row.unqualified_items)
					? row.unqualified_items
					: undefined,
			raw: row,
		}));
		return { ok: true, items: rows, total: extractTotal(res.data) };
	} catch (e) {
		return { ok: false, items: [], msg: e instanceof Error ? e.message : String(e) };
	}
}

function normalizeTrend(value: unknown): Array<{ date: string; value: number }> {
	const trends: Array<{ date: string; value: number }> = [];
	for (const row of normalizeArray<Record<string, unknown>>(value)) {
		const nested = normalizeArray<Record<string, unknown>>(row.list ?? row.items ?? row.data);
		if (nested.length > 0) {
			for (const item of nested) {
				const date = pickString(item, ['date', 'day', 'time']);
				if (date) trends.push({ date, value: pickNumber(item, ['unqualified_pic', 'value', 'count']) });
			}
			continue;
		}
		const date = pickString(row, ['date', 'day', 'time']);
		if (date) trends.push({ date, value: pickNumber(row, ['unqualified_pic', 'value', 'count']) });
	}
	return trends;
}

export async function fetchTimedSnapshotPlans(options: FetchTimedSnapshotPlansOptions = {}): Promise<TimedSnapshotPageResult<TimedSnapshotPlan>> {
	try {
		const res = await callInternal<unknown>({
			short_name: 'standard',
			action: '/dianjian/plan/page',
			method: 'GET',
			query: cleanQuery({
				count: options.count ?? 20,
				page: options.page ?? 1,
				type: 0,
				close_status: options.status,
				store_id: options.storeId,
			}),
			av: '1',
		});
		if (Number(res.code) !== 0) return { ok: false, items: [], msg: responseMsg(res.code, res.msg) };
		return { ok: true, items: normalizeArray<TimedSnapshotPlan>(res.data), total: extractTotal(res.data) };
	} catch (e) {
		return { ok: false, items: [], msg: e instanceof Error ? e.message : String(e) };
	}
}

export async function fetchTimedSnapshotPlanList(): Promise<TimedSnapshotPageResult<TimedSnapshotPlan>> {
	try {
		const res = await callInternal<unknown>({
			short_name: 'standard',
			action: '/dianjian/plan/list',
			method: 'GET',
			query: { type: 0 },
			av: '1',
		});
		if (Number(res.code) !== 0) return { ok: false, items: [], msg: responseMsg(res.code, res.msg) };
		return { ok: true, items: normalizeArray<TimedSnapshotPlan>(res.data) };
	} catch (e) {
		return { ok: false, items: [], msg: e instanceof Error ? e.message : String(e) };
	}
}

export async function fetchTimedSnapshotPlanInfo(planId: string | number): Promise<TimedSnapshotActionResult & { plan?: TimedSnapshotPlan }> {
	try {
		const res = await callInternal<TimedSnapshotPlan>({
			short_name: 'standard',
			action: '/dianjian/plan/info',
			method: 'GET',
			query: { plan_id: planId, type: 0 },
			av: '1',
		});
		if (Number(res.code) !== 0) return { ok: false, msg: responseMsg(res.code, res.msg) };
		return { ok: true, msg: res.msg, plan: res.data };
	} catch (e) {
		return { ok: false, msg: e instanceof Error ? e.message : String(e) };
	}
}

export async function fetchTimedSnapshotPictures(options: FetchTimedSnapshotPicturesOptions): Promise<TimedSnapshotPageResult<TimedSnapshotPicture>> {
	try {
		const tabQuery = options.tab === 'overdue'
			? { exceed_status: 1, handle_status: 0 }
			: options.tab === 'unprocessed'
				? { handle_status: 0 }
				: { handle_status: 1 };
		const res = await callInternal<unknown>({
			short_name: 'standard',
			action: '/dianjian/ordinary/pic/page',
			method: 'GET',
			query: cleanQuery({
				count: options.count ?? 20,
				page: options.page ?? 1,
				start_date: options.startDate,
				end_date: options.endDate,
				store_ids: options.storeIds,
				plan_id: options.planId,
				ok_status: options.okStatus,
				item_id: options.itemId,
				user_id: options.userId,
				...tabQuery,
			}),
			av: '2',
		});
		if (Number(res.code) !== 0) return { ok: false, items: [], msg: responseMsg(res.code, res.msg) };
		return { ok: true, items: normalizeArray<TimedSnapshotPicture>(res.data), total: extractTotal(res.data) };
	} catch (e) {
		return { ok: false, items: [], msg: e instanceof Error ? e.message : String(e) };
	}
}

export async function fetchTimedSnapshotStores(): Promise<TimedSnapshotStore[]> {
	const res = await callInternal<unknown>({
		short_name: 'base',
		action: '/store/visible_stores',
		method: 'GET',
	});
	if (Number(res.code) !== 0) throw new Error(responseMsg(res.code, res.msg));
	return normalizeArray<TimedSnapshotStore>(res.data);
}

export async function fetchTimedSnapshotDevices(storeIds: Array<string | number>): Promise<TimedSnapshotDevice[]> {
	const results = await Promise.all(storeIds.map(async (storeId) => {
		const res = await callInternal<unknown>({
			short_name: 'base',
			action: '/fastDevice/channel_list',
			method: 'POST',
			body: { store_id: storeId, with_yuzhiwei_status: 1 },
		});
		if (Number(res.code) !== 0) throw new Error(responseMsg(res.code, res.msg));
		return normalizeArray<TimedSnapshotDevice>(res.data).map((device) => ({
			...device,
			store_id: device.store_id ?? storeId,
		}));
	}));
	const flattened: TimedSnapshotDevice[] = [];
	for (const result of results) {
		for (const device of result) flattened.push(device);
	}
	return flattened;
}

export async function fetchTimedSnapshotUsers(): Promise<TimedSnapshotUser[]> {
	const res = await callInternal<unknown>({
		short_name: 'standard',
		action: '/dianjian/users/list',
		method: 'GET',
		av: '1',
	});
	if (Number(res.code) !== 0) throw new Error(responseMsg(res.code, res.msg));
	return normalizeArray<TimedSnapshotUser>(res.data);
}

export async function fetchTimedSnapshotExecuteUsers(
	storeIds: Array<string | number>,
	userIds: Array<string | number>,
): Promise<unknown> {
	const res = await callInternal<unknown>({
		short_name: 'standard',
		action: '/dianjian/execute/users',
		method: 'POST',
		body: {
			store_ids: storeIds.map((item) => String(item)).join(','),
			user_ids: userIds.map((item) => String(item)).join(','),
		},
		av: '1',
	});
	if (Number(res.code) !== 0) throw new Error(responseMsg(res.code, res.msg));
	return res.data;
}

export async function fetchTimedSnapshotTemplateItems(): Promise<TimedSnapshotTemplateItem[]> {
	const res = await callInternal<unknown>({
		short_name: 'base',
		action: '/template/list_all',
		method: 'GET',
		av: '2',
	});
	if (Number(res.code) !== 0) throw new Error(responseMsg(res.code, res.msg));
	return collectTemplateItems(res.data);
}

function collectTemplateItems(data: unknown): TimedSnapshotTemplateItem[] {
	const items: TimedSnapshotTemplateItem[] = [];
	const visit = (
		value: unknown,
		templateId?: string | number,
		categoryId?: string | number,
	): void => {
		const records = Array.isArray(value) ? value : normalizeArray<Record<string, unknown>>(value);
		for (const record of records) {
			const row = asRecord(record);
			if (!row) continue;
			const nextTemplateId = pickString(row, ['template_id', 'templates_id'], '') || templateId || pickString(row, ['id'], '');
			const nextCategoryId = pickString(row, ['category_id', 'categories_id', 'parent_id'], '') || categoryId;
			const itemId = pickString(row, ['item_id', 'items_id'], '');
			const itemName = pickString(row, ['item_name', 'title', 'name'], '');
			const finalTemplateId = pickString(row, ['template_id', 'templates_id'], '') || templateId;
			const finalCategoryId = pickString(row, ['category_id', 'categories_id'], '') || categoryId;
			if (itemId && itemName && finalTemplateId != null && finalTemplateId !== '' && finalCategoryId != null && finalCategoryId !== '') {
				items.push({
					item_id: numericId(itemId),
					item_name: itemName,
					category_id: numericId(String(finalCategoryId)),
					template_id: numericId(String(finalTemplateId)),
					raw: row,
				});
			}
			for (const key of ['content', 'children', 'child', 'items', 'list', 'categories', 'item_list'] as const) {
				if (row[key] != null) visit(row[key], nextTemplateId, nextCategoryId);
			}
		}
	};
	visit(data);
	const seen = new Set<string>();
	return items.filter((item) => {
		const key = `${item.template_id}:${item.category_id}:${item.item_id}`;
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
}

export function buildTimedSnapshotTaskBody(req: CreateTimedSnapshotTaskRequest): Record<string, unknown> {
	const title = req.title.trim();
	const times = normalizeTimes(req.times);
	if (!title) throw new Error('Task name is required');
	if (!req.startDate || !req.endDate) throw new Error('Execution date is required');
	if (req.executeWeek.length === 0) throw new Error('Execution cycle is required');
	if (req.executeMonth.length === 0) throw new Error('Execution month is required');
	if (req.items.length === 0) throw new Error('Assessment template is required');
	if (req.stores.length === 0) throw new Error('Store is required');
	if (req.devices.length === 0) throw new Error('Device is required');
	if (req.users.length === 0) throw new Error('Executor is required');
	if (times.length === 0) throw new Error('Execution time is required');

	const storeIds = req.stores.map((store) => numericId(store.store_id));
	const items = req.items.map((item) => ({
		item_id: item.item_id,
		item_name: item.item_name,
		category_id: item.category_id,
		template_id: item.template_id,
	}));
	const yuzhiweis = req.devices.map((device) => ({
		device_auto_id: device.device_auto_id,
		channel_id: Number(device.channel_id ?? 1),
		items: [{
			name: device.yuzhiwei_name || device.alias || device.name || 'Equipment Location',
			execute_times: times.join(','),
		}],
	}));
	const usersByStores = req.users.map((user) => ({
		user_id: numericId(user.user_id),
		stores: storeIds,
	}));

	return {
		type: 0,
		title,
		start_date: req.startDate,
		end_date: req.endDate,
		execute_week: req.executeWeek.join(','),
		execute_month: req.executeMonth.join(','),
		items: JSON.stringify(items),
		yuzhiweis: JSON.stringify(yuzhiweis),
		users_by_stores: JSON.stringify(usersByStores),
	};
}

export async function createTimedSnapshotTask(req: CreateTimedSnapshotTaskRequest): Promise<TimedSnapshotActionResult> {
	let body: Record<string, unknown>;
	try {
		body = buildTimedSnapshotTaskBody(req);
	} catch (e) {
		return { ok: false, msg: e instanceof Error ? e.message : String(e) };
	}
	try {
		await fetchTimedSnapshotExecuteUsers(req.stores.map((store) => store.store_id), req.users.map((user) => user.user_id));
		const res = await callInternal<{ task_id?: string | number; plan_id?: string | number }>({
			short_name: 'standard',
			action: '/dianjian/plan/common/add',
			method: 'POST',
			body,
			av: '1',
		});
		if (Number(res.code) !== 0) return { ok: false, msg: responseMsg(res.code, res.msg) };
		let planId = res.data?.plan_id;
		if (planId == null && res.data?.task_id != null) {
			const plans = await fetchTimedSnapshotPlans({ page: 1, count: 50 });
			const matched = plans.items.find((plan) => getTimedSnapshotPlanTitle(plan) === req.title.trim());
			planId = matched ? getTimedSnapshotPlanId(matched) : undefined;
		}
		return { ok: true, msg: res.msg, planId: planId ?? res.data?.task_id };
	} catch (e) {
		return { ok: false, msg: e instanceof Error ? e.message : String(e) };
	}
}

export async function setTimedSnapshotTaskEnabled(planId: string | number, enabled: boolean): Promise<TimedSnapshotActionResult> {
	try {
		const res = await callInternal({
			short_name: 'standard',
			action: enabled ? '/dianjian/plan/open' : '/dianjian/plan/close',
			method: 'POST',
			body: { plan_id: planId },
			av: '1',
		});
		if (Number(res.code) !== 0) return { ok: false, msg: responseMsg(res.code, res.msg) };
		return { ok: true, msg: res.msg };
	} catch (e) {
		return { ok: false, msg: e instanceof Error ? e.message : String(e) };
	}
}

export async function deleteTimedSnapshotTask(planId: string | number): Promise<TimedSnapshotActionResult> {
	try {
		const res = await callInternal({
			short_name: 'standard',
			action: '/dianjian/plan/destroy',
			method: 'POST',
			body: { plan_id: planId },
			av: '1',
		});
		if (Number(res.code) !== 0) return { ok: false, msg: responseMsg(res.code, res.msg) };
		return { ok: true, msg: res.msg };
	} catch (e) {
		return { ok: false, msg: e instanceof Error ? e.message : String(e) };
	}
}

export async function handleTimedSnapshotPicture(req: HandleTimedSnapshotPictureRequest): Promise<TimedSnapshotActionResult> {
	try {
		const itemRaw = asRecord(req.item?.raw);
		const item = req.item
			? JSON.stringify({
				items_id: req.item.item_id,
				title: req.item.item_name,
				category_id: req.item.category_id,
				template_id: req.item.template_id,
				...(itemRaw?.value_type != null ? { value_type: itemRaw.value_type } : {}),
				...(itemRaw?.score != null ? { score: itemRaw.score } : {}),
			})
			: undefined;
		const res = await callInternal({
			short_name: 'standard',
			action: '/dianjian/pic/handle',
			method: 'POST',
			body: cleanBody({
				id: req.id,
				handle_status: req.handleStatus,
				item,
				user_ids: req.userIds?.join(','),
				description: req.description,
				without_event: req.withoutEvent ? 1 : undefined,
				score: req.score,
			}),
			av: '1',
		});
		if (Number(res.code) !== 0) return { ok: false, msg: responseMsg(res.code, res.msg) };
		return { ok: true, msg: res.msg };
	} catch (e) {
		return { ok: false, msg: e instanceof Error ? e.message : String(e) };
	}
}
